import React, { useState } from 'react'
import { TrendingDown, TrendingUp, X, Info } from 'lucide-react'
import { useDepreciationRules } from '../../../hooks/useDepreciationRules'
import { formatCurrency } from '../../../utils/formatters'
import { Button } from '../../ui/Button'
import { cn } from '../../../utils/cn'

interface DepreciationPanelProps {
  fipeValue: number
  mileage: number
  modelYear: number
  condition: string
  onApplySuggested: (value: number) => void
}

export const DepreciationPanel: React.FC<DepreciationPanelProps> = ({
  fipeValue,
  mileage,
  modelYear,
  condition,
  onApplySuggested,
}) => {
  const { calculate } = useDepreciationRules()
  const { appliedRules } = calculate(mileage, modelYear, condition, fipeValue)
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set())

  const toggleExclude = (id: string) => {
    setExcludedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const activeRules = appliedRules.filter(r => !excludedIds.has(r.id))
  const activeTotalPct = activeRules.reduce((sum, r) => sum + r.percentage, 0)
  const activeSuggestedValue = Math.round(fipeValue * (1 + activeTotalPct / 100))

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* FIPE reference */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-surface border-b border-border">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Referência FIPE
          </span>
        </div>
        <span className="text-sm font-bold font-mono text-foreground">
          {formatCurrency(fipeValue)}
        </span>
      </div>

      {appliedRules.length > 0 ? (
        <div className="px-4 py-3 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Fatores detectados
          </p>

          {appliedRules.map(rule => {
            const excluded = excludedIds.has(rule.id)
            return (
              <div
                key={rule.id}
                className={cn(
                  'flex items-center justify-between gap-3 px-3 py-2 rounded-md border transition-colors',
                  excluded ? 'bg-surface border-border opacity-50' : 'bg-card border-border'
                )}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {rule.percentage < 0 ? (
                    <TrendingDown className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
                  ) : (
                    <TrendingUp className="h-3.5 w-3.5 text-accent flex-shrink-0" />
                  )}
                  <span
                    className={cn(
                      'text-sm truncate',
                      excluded ? 'line-through text-muted-foreground' : 'text-foreground'
                    )}
                  >
                    {rule.label}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className={cn(
                      'text-sm font-semibold font-mono tabular-nums',
                      excluded
                        ? 'text-muted-foreground'
                        : rule.percentage < 0
                        ? 'text-destructive'
                        : 'text-accent'
                    )}
                  >
                    {rule.percentage > 0 ? '+' : ''}
                    {rule.percentage}%
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleExclude(rule.id)}
                    className="p-0.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    title={excluded ? 'Reincluir fator' : 'Remover fator'}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )
          })}

          {activeRules.length > 0 && (
            <div className="pt-2 border-t border-border space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Ajuste total
                </span>
                <span
                  className={cn(
                    'text-sm font-bold font-mono tabular-nums',
                    activeTotalPct < 0 ? 'text-destructive' : 'text-accent'
                  )}
                >
                  {activeTotalPct > 0 ? '+' : ''}
                  {activeTotalPct.toFixed(1)}%
                </span>
              </div>

              <div className="flex items-center justify-between px-3 py-2.5 bg-surface border border-border rounded-md">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Valor Sugerido</p>
                  <p className="text-base font-bold text-foreground font-mono">
                    {formatCurrency(activeSuggestedValue)}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => onApplySuggested(activeSuggestedValue)}
                >
                  Aplicar
                </Button>
              </div>
            </div>
          )}

          {activeRules.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-1">
              Todos os fatores foram removidos. O valor FIPE será usado como referência.
            </p>
          )}
        </div>
      ) : (
        <div className="px-4 py-3">
          <p className="text-xs text-muted-foreground text-center py-1">
            Nenhuma regra de desvalorização se aplica a este veículo.
          </p>
        </div>
      )}
    </div>
  )
}
